import { useId } from "react";

export type RadioVariant = "default" | "brand" | "signature";

const VARIANT_RING_CLASSES: Record<RadioVariant, string> = {
  default: "border-content-primary",
  brand: "border-brand-primary",
  signature: "border-brand-primary",
};

const VARIANT_DOT_CLASSES: Record<RadioVariant, string> = {
  default: "bg-content-primary",
  brand: "bg-brand-primary",
  signature: "bg-brand-gradient",
};

export interface RadioProps {
  checked: boolean;
  onSelect: () => void;
  label?: string;

  description?: string;

  variant?: RadioVariant;
  disabled?: boolean;
  id?: string;

  "aria-label"?: string;

  tabIndex?: number;

  buttonRef?: (node: HTMLButtonElement | null) => void;
}

export function Radio({
  checked,
  onSelect,
  label,
  description,
  variant = "default",
  disabled = false,
  id,
  "aria-label": ariaLabel,
  tabIndex,
  buttonRef,
}: Readonly<RadioProps>) {
  const autoId = useId();
  const resolvedId = id ?? autoId;

  const dot = (
    <button
      id={resolvedId}
      ref={buttonRef}
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      tabIndex={tabIndex}
      onClick={onSelect}
      className={[
        "relative grid size-4 shrink-0 place-items-center rounded-full border bg-surface-base transition-colors",
        "btn-focus-ring",
        checked ? VARIANT_RING_CLASSES[variant] : "border-border-subtle",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      ].join(" ")}
    >
      {checked && <span className={["size-2 rounded-full", VARIANT_DOT_CLASSES[variant]].join(" ")} />}
    </button>
  );

  if (!label) return dot;

  return (
    <label
      htmlFor={resolvedId}
      className={["flex items-start gap-2", disabled ? "cursor-not-allowed" : "cursor-pointer"].join(" ")}
    >
      {}
      <span className="mt-0.5">{dot}</span>
      <span>
        <span className="block text-sm font-medium text-content-secondary">{label}</span>
        {description && <span className="block text-xs text-content-tertiary">{description}</span>}
      </span>
    </label>
  );
}
